import { DateTime } from 'luxon';
import * as Crypto from 'expo-crypto';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import {
    SERVICE_FLOW_ITEM_TYPES,
    SERVICE_FLOW_LIMITS,
    SERVICE_FLOW_ERROR_CODES,
} from '@/config/serviceFlows';

/**
 * Service Flow v2 pure helpers (spec §4, §7, §10, §12.5).
 *
 * Everything here is pure/deterministic — no Supabase calls, no hooks.
 * Timing: `startOffsetMinutes` is an elapsed-minute offset from the
 * occurrence's startTime. Display resolves through the ORGANIZATION
 * timezone via Luxon; never the device timezone (spec §7.1).
 */

export const newFlowItemID = () => Crypto.randomUUID();

// ---------- Item normalization ----------

/**
 * Normalize a raw template or instance item into the discriminated draft/result
 * shape used across the UI (spec §4, §10.1). Unknown types fall back to AGENDA
 * so legacy/untyped rows stay readable.
 */
export const normalizeFlowItem = (raw = {}, index = 0) => {
    const type = SERVICE_FLOW_ITEM_TYPES[raw?.type] ? raw.type
        : (raw?.taskID || raw?.taskInstanceID ? 'TASK' : 'AGENDA');
    return {
        id: raw.id || newFlowItemID(),
        type,
        title: raw.title || '',
        description: raw.description ?? null, // UI label: Notes
        position: raw.position ?? index,
        startOffsetMinutes: raw.startOffsetMinutes ?? null,
        durationMinutes: raw.durationMinutes ?? null,
        showTime: !!raw.showTime,
        taskID: type === 'TASK' ? (raw.taskID ?? raw.TaskInstance?.taskID ?? null) : null,
        volunteerTeamID: type === 'TASK' ? (raw.volunteerTeamID ?? raw.TaskInstance?.volunteerTeamID ?? null) : null,
        templateItemID: raw.templateItemID ?? null,
        taskInstanceID: raw.taskInstanceID ?? null,
        resolvedStartTime: raw.resolvedStartTime ?? null,
        Task: raw.Task ?? raw.TaskInstance?.Task ?? null,
        VolunteerTeam: raw.VolunteerTeam ?? raw.TaskInstance?.VolunteerTeam ?? null,
        TaskInstance: raw.TaskInstance ?? null,
        taskRestricted: !!raw.taskRestricted || raw.taskState === 'RESTRICTED',
        taskState: raw.taskState ?? null,
    };
};

export const normalizeFlowItems = (items = []) =>
    (items || []).map((it, i) => normalizeFlowItem(it, i))
        .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));

/**
 * Normalize the `get_service_flow_instance` RPC payload into an explicit
 * snapshot object (spec §10.3).
 */
export const normalizeInstanceSnapshot = (payload = {}) => ({
    eventInstanceID: payload.eventInstanceID ?? null,
    locationID: payload.locationID ?? null,
    flowID: payload.flowID ?? null,
    availableLocations: payload.availableLocations ?? [],
    organizationID: payload.organizationID ?? null,
    revision: payload.revision ?? 0,
    initialized: !!payload.initialized,
    customized: !!payload.customized,
    protectedAt: payload.protectedAt ?? null,
    templateID: payload.templateID ?? null,
    templateVersion: payload.templateVersion ?? null,
    currentEventTemplateID: payload.currentEventTemplateID ?? null,
    currentEventTemplateVersion: payload.currentEventTemplateVersion ?? null,
    timezone: payload.timezone || 'UTC',
    items: normalizeFlowItems(payload.items || []),
    taskContext: {
        eventInstance: payload.taskContext?.eventInstance ?? null,
        taskInstances: payload.taskContext?.taskInstances ?? [],
    },
});

/**
 * TaskCard adapter (spec §10.3): supply `TaskInstance.EventInstance` from the
 * shared taskContext (with sibling TaskInstances for conflict detection) and
 * flatten Assignees so `id` = Person.id while preserving the join identity as
 * `assignmentID` plus personID/assignmentResponse/position.
 */
export const adaptSnapshotTaskInstances = (snapshot) => {
    const contextInstances = snapshot?.taskContext?.taskInstances || [];
    const contextEventInstance = snapshot?.taskContext?.eventInstance || null;
    const eventInstanceForCards = contextEventInstance
        ? { ...contextEventInstance, TaskInstances: contextInstances }
        : null;

    const adaptAssignees = (taskInstance) => ({
        ...taskInstance,
        EventInstance: taskInstance.EventInstance || eventInstanceForCards,
        Assignees: (taskInstance.Assignees || []).map(a => {
            const person = a.Person || {};
            return {
                ...a,
                ...person,
                // Person.id wins for TaskCard identity; the join row id is kept.
                id: person.id || a.personID || a.id,
                assignmentID: a.assignmentID ?? a.id,
                personID: a.personID ?? person.id,
                assignmentResponse: a.assignmentResponse,
                position: a.position,
            };
        }),
    });

    const byID = new Map(contextInstances.map(t => [t.id, t]));
    return (snapshot?.items || []).map(item => {
        if (item.type !== 'TASK') return item;
        const rawTI = item.TaskInstance || byID.get(item.taskInstanceID) || null;
        return {
            ...item,
            Task: item.Task ?? rawTI?.Task ?? null,
            VolunteerTeam: item.VolunteerTeam ?? rawTI?.VolunteerTeam ?? null,
            taskID: item.taskID ?? rawTI?.taskID ?? null,
            volunteerTeamID: item.volunteerTeamID ?? rawTI?.volunteerTeamID ?? null,
            TaskInstance: rawTI ? adaptAssignees(rawTI) : null,
        };
    });
};

// ---------- Timing display (spec §7) ----------

/**
 * Template-context relative label: `10 min before` / `At start` / `20 min
 * after`. NULL offset → `Time not set` (spec §7.2, §7.3).
 */
export const formatOffsetLabel = (startOffsetMinutes) => {
    if (startOffsetMinutes === null || startOffsetMinutes === undefined) return 'Time not set';
    if (startOffsetMinutes === 0) return 'At start';
    return `${Math.abs(startOffsetMinutes)} min ${startOffsetMinutes < 0 ? 'before' : 'after'}`;
};

export const formatDurationLabel = (durationMinutes) => {
    if (durationMinutes === null || durationMinutes === undefined) return 'Duration not set';
    return `${durationMinutes} min`;
};

/**
 * Resolve an offset against an occurrence start instant. Returns a Luxon
 * DateTime in the organization timezone, or null when either input is
 * missing (all-day/startless occurrences never invent midnight — spec §7.3).
 */
export const resolveOffsetStart = (occurrenceStartISO, startOffsetMinutes, timezone = 'UTC') => {
    if (!occurrenceStartISO || startOffsetMinutes === null || startOffsetMinutes === undefined) return null;
    const start = DateTime.fromISO(occurrenceStartISO, { zone: 'utc' }).setZone(timezone || 'UTC');
    if (!start.isValid) return null;
    return start.plus({ minutes: startOffsetMinutes });
};

/**
 * Gutter label for an occurrence row. Prefers the server's resolvedStartTime;
 * otherwise resolves occurrence start + offset. Returns 'Time not set' when
 * nothing can be resolved. Flags a "+Nd" suffix when the resolved time lands
 * on a later day than the occurrence start (cross-midnight, spec §7.1).
 */
export const formatInstanceTimeLabel = ({ item, occurrenceStartISO, timezone = 'UTC', allDay = false }) => {
    if (allDay) return { label: formatOffsetLabel(item?.startOffsetMinutes), dayOffset: 0 };
    if (item?._legacyTime) {
        const legacy = DateTime.fromFormat(item._legacyTime.slice(0, 5), 'HH:mm');
        if (legacy.isValid) return { label: legacy.toFormat('h:mm a'), dayOffset: 0 };
    }
    let resolved = null;
    if (item?.resolvedStartTime) {
        resolved = DateTime.fromISO(item.resolvedStartTime, { zone: 'utc' }).setZone(timezone || 'UTC');
        if (!resolved?.isValid) resolved = null;
    }
    if (!resolved) resolved = resolveOffsetStart(occurrenceStartISO, item?.startOffsetMinutes, timezone);
    if (!resolved) return { label: 'Time not set', dayOffset: 0 };

    let dayOffset = 0;
    const start = occurrenceStartISO ? DateTime.fromISO(occurrenceStartISO, { zone: 'utc' }).setZone(timezone || 'UTC') : null;
    if (start?.isValid) {
        dayOffset = Math.floor(resolved.startOf('day').diff(start.startOf('day'), 'days').days);
    }
    return { label: resolved.toFormat('h:mm a'), dayOffset };
};

/**
 * Convert a user-picked wall-clock time in the ORGANIZATION timezone back to
 * an elapsed-minute offset from the occurrence start (spec §7.1 — never the
 * device timezone).
 */
export const offsetFromOrgWallTime = ({ occurrenceStartISO, pickedDate, timezone = 'UTC' }) => {
    if (!occurrenceStartISO || !pickedDate) return null;
    const start = DateTime.fromISO(occurrenceStartISO, { zone: 'utc' }).setZone(timezone || 'UTC');
    const picked = DateTime.fromJSDate(pickedDate, { zone: timezone || 'UTC' });
    if (!start.isValid || !picked.isValid) return null;
    return Math.round(picked.diff(start, 'minutes').minutes);
};

// ---------- Span / ordering helpers ----------

/**
 * Earliest known start → latest known end, in offset minutes. Not the sum of
 * durations (spec §7.3). Returns null when nothing is scheduled.
 */
export const getFlowSpanOffsets = (items = []) => {
    let earliest = null;
    let latest = null;
    for (const it of items) {
        const o = it?.startOffsetMinutes;
        if (o === null || o === undefined) continue;
        const end = o + (it?.durationMinutes ?? 0);
        earliest = earliest === null ? o : Math.min(earliest, o);
        latest = latest === null ? end : Math.max(latest, end);
    }
    if (earliest === null) return null;
    return { startOffset: earliest, endOffset: latest };
};

/** "10m before start – 60m after" style span label for template cards. */
export const formatFlowSpanLabel = (items = []) => {
    const span = getFlowSpanOffsets(items);
    if (!span) return 'No scheduled times';
    const fmt = (o) => o === 0 ? 'start' : `${Math.abs(o)}m ${o < 0 ? 'before' : 'after'}`;
    return `${fmt(span.startOffset)} – ${fmt(span.endOffset)}`;
};

/**
 * Non-blocking warnings for non-chronological times and overlapping
 * durations (spec §4.6). Returns a list of human-readable strings.
 */
export const getTimingWarnings = (items = []) => {
    const warnings = [];
    const timed = items
        .map((it, i) => ({ it, i }))
        .filter(({ it }) => it?.startOffsetMinutes !== null && it?.startOffsetMinutes !== undefined)
        .sort((a, b) => a.it.position - b.it.position);

    let prevTimed = null;
    for (const { it } of timed) {
        const start = it.startOffsetMinutes;
        const end = start + (it.durationMinutes ?? 0);
        if (prevTimed) {
            if (start < prevTimed.startOffset) {
                warnings.push(`"${it.title || 'Untitled'}" is scheduled before an item that comes earlier in the flow.`);
            } else if (prevTimed.end !== null && start < prevTimed.end) {
                warnings.push(`"${it.title || 'Untitled'}" overlaps with "${prevTimed.title || 'the previous item'}".`);
            }
        }
        prevTimed = { title: it.title, startOffset: start, end: it.durationMinutes != null ? end : null };
    }
    return warnings;
};

/**
 * Sequential start offsets in minutes (spec §7.4): an item with an explicit
 * startOffsetMinutes keeps it; every other item starts when the preceding
 * item's span ends — first item at 0, unset durations contributing 0.
 * Returns id → minutes. This is what `showTime` displays on the timeline.
 */
export const deriveFlowOffsets = (items = []) => {
    const offsets = new Map();
    let running = 0;
    for (const it of [...(items || [])].sort((a, b) => (a.position ?? 0) - (b.position ?? 0))) {
        const start = Number.isInteger(it?.startOffsetMinutes) ? it.startOffsetMinutes : running;
        offsets.set(it.id, start);
        running = start + (Number.isInteger(it?.durationMinutes) ? it.durationMinutes : 0);
    }
    return offsets;
};

/**
 * Focused-editor modal title, e.g. `New Service Flow Agenda Item` /
 * `Edit Service Flow Task Item` / `New Service Flow Worship Set`.
 */
export const flowItemEditorTitle = (item, isNew) => {
    const noun = item?.type === 'WORSHIP'
        ? 'Worship Set'
        : `${SERVICE_FLOW_ITEM_TYPES[item?.type]?.label || 'Agenda'} Item`;
    return `${isNew ? 'New' : 'Edit'} Service Flow ${noun}`;
};

// ---------- Validation (spec §4.1, §10.1) ----------

/**
 * Validate a draft/editing item. Returns { field: message } — empty when valid.
 */
export const validateFlowItem = (item = {}) => {
    const errors = {};
    const title = (item.title || '').trim();
    if (title.length < SERVICE_FLOW_LIMITS.titleMinLength) {
        errors.title = 'Title is required';
    } else if (title.length > SERVICE_FLOW_LIMITS.titleMaxLength) {
        errors.title = `Title must be ${SERVICE_FLOW_LIMITS.titleMaxLength} characters or fewer`;
    }
    if (item.startOffsetMinutes !== null && item.startOffsetMinutes !== undefined) {
        if (!Number.isInteger(item.startOffsetMinutes)
            || item.startOffsetMinutes < SERVICE_FLOW_LIMITS.offsetMinMinutes
            || item.startOffsetMinutes > SERVICE_FLOW_LIMITS.offsetMaxMinutes) {
            errors.startOffsetMinutes = 'Enter a whole number of minutes';
        }
    }
    if (item.durationMinutes !== null && item.durationMinutes !== undefined) {
        if (!Number.isInteger(item.durationMinutes)
            || item.durationMinutes < SERVICE_FLOW_LIMITS.durationMinMinutes
            || item.durationMinutes > SERVICE_FLOW_LIMITS.durationMaxMinutes) {
            errors.durationMinutes = `Duration must be ${SERVICE_FLOW_LIMITS.durationMinMinutes}–${SERVICE_FLOW_LIMITS.durationMaxMinutes} minutes`;
        }
    }
    if ((item.description || '').length > SERVICE_FLOW_LIMITS.notesMaxLength) {
        errors.description = 'Notes are too long';
    }
    if (item.type === 'TASK' && !item.taskID && !item.taskInstanceID) {
        errors.taskID = 'Choose a Service Flow task';
    }
    return errors;
};

// ---------- RPC input builders (spec §10.1/§10.2) ----------

/**
 * Strip client-only keys — the server rejects unknown input keys such as
 * assignees or taskInstanceID (spec §10.1).
 */
export const buildTemplateItemInput = (item, index) => ({
    id: item.id,
    type: item.type,
    title: (item.title || '').trim(),
    description: item.description || null,
    position: index ?? item.position ?? 0,
    startOffsetMinutes: item.startOffsetMinutes ?? null,
    durationMinutes: item.durationMinutes ?? null,
    showTime: !!item.showTime,
    taskID: item.type === 'TASK' ? (item.taskID || null) : null,
    volunteerTeamID: item.type === 'TASK' ? (item.volunteerTeamID || null) : null,
});

/**
 * Instance-item input: same fields; `position` is an insertion index only on
 * creation (updates reorder via the dedicated RPC).
 */
export const buildInstanceItemInput = (item, { isNew = false } = {}) => {
    const input = buildTemplateItemInput(item, item.position);
    if (!isNew) delete input.position;
    return input;
};

// ---------- Source/status labels (spec §3.5) ----------

export const sourceStatusLabel = (snapshot) => {
    if (snapshot?.protectedAt) return 'Assignments protect this date';
    if (snapshot?.customized) return 'Customized for this date';
    return 'Follows template';
};

// ---------- Error-code helpers (spec §10.4) ----------

const errorText = (e) => `${e?.code || ''} ${e?.message || ''} ${e?.details || ''}`;

export const isServiceFlowConflictError = (e) =>
    e?.code === '40001' || errorText(e).includes(SERVICE_FLOW_ERROR_CODES.conflict);

export const isServiceFlowUpgradeError = (e) =>
    e?.code === '0A000' || errorText(e).includes(SERVICE_FLOW_ERROR_CODES.upgradeRequired);

export const isServiceFlowTaskInUseError = (e) =>
    errorText(e).includes(SERVICE_FLOW_ERROR_CODES.taskInUse);

export const isServiceFlowArchivedError = (e) =>
    errorText(e).includes(SERVICE_FLOW_ERROR_CODES.templateArchived);

export const isServiceFlowScopeDeniedError = (e) =>
    errorText(e).includes(SERVICE_FLOW_ERROR_CODES.sharedScopeDenied);

export const isServiceFlowDeniedError = (e) =>
    e?.code === '42501' || isServiceFlowScopeDeniedError(e);

export const isServiceFlowValidationError = (e) => e?.code === '22023';

export const isServiceFlowDeadlockError = (e) => e?.code === '40P01';

// ---------- Invalidation (spec §12.5) ----------

/**
 * Shared invalidation for service-flow-linked task mutations. Both TaskCard
 * and the task-instance detail screen use this so the parent's flow query
 * refetches (nested TaskCard data, protection status) after assignment,
 * response, or completion changes.
 */
export const invalidateServiceFlowForTaskInstance = (queryClient, { eventInstanceID, organizationID, accountID } = {}) => {
    if (!eventInstanceID) return;
    if (organizationID && accountID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.serviceFlows.instance(organizationID, accountID, eventInstanceID) });
    }
    // Legacy (v1) read key — cheap no-op when nothing is mounted on it.
    queryClient.invalidateQueries({ queryKey: QueryKeys.serviceFlows.byInstance(eventInstanceID) });
};

/**
 * Full invalidation after an instance flow structural edit / reset / reorder.
 */
export const invalidateServiceFlowInstanceQueries = (queryClient, { eventInstanceID, eventID, organizationID, accountID } = {}) => {
    if (!eventInstanceID) return;
    invalidateServiceFlowForTaskInstance(queryClient, { eventInstanceID, organizationID, accountID });
    queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.byId(eventInstanceID) });
    if (eventID && organizationID && accountID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.serviceFlows.event(organizationID, accountID, eventID) });
        queryClient.invalidateQueries({ queryKey: QueryKeys.eventInstances.upcomingVolunteerTasks(eventID) });
    }
    queryClient.invalidateQueries({ queryKey: QueryKeys.taskInstances.individual() });
    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
};

/**
 * Invalidation after template save/archive/duplicate/attachment changes.
 */
export const invalidateServiceFlowTemplateQueries = (queryClient, { organizationID, accountID, templateID: _templateID, eventID } = {}) => {
    if (!organizationID || !accountID) return;
    queryClient.invalidateQueries({ queryKey: QueryKeys.serviceFlows.scoped(organizationID, accountID) });
    if (eventID) {
        queryClient.invalidateQueries({ queryKey: QueryKeys.events.byId(eventID) });
    }
    queryClient.invalidateQueries({ queryKey: ['dashboardData'] });
};

/**
 * True when stored notes contain HTML markup (the web rich-text editor stores
 * TipTap HTML in `description`; plain-text notes pass through untouched).
 */
export const looksLikeHtml = (text) => /<[a-z][^>]*>/i.test((text || '').trim());

/**
 * Best-effort HTML → plain text for plain-text surfaces — the native notes
 * field edits stripped text, and the PDF falls back to it for legacy rows.
 */
export const htmlToPlainText = (html) => {
    if (!looksLikeHtml(html)) return html || '';
    return html
        .replace(/<br\s*\/?>(?![^<]*>)/gi, '\n')
        .replace(/<li[^>]*>/gi, '• ')
        .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&#0?39;|&apos;/g, "'")
        .replace(/&quot;/g, '"')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
};
